import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, FolderKanban, Search, ArrowUpDown, ArrowUp, ArrowDown, Settings2, Check, Archive, ArchiveRestore } from 'lucide-react';
import api from '../lib/api';
import { fmtMoneyLabel, fmtDate } from '../lib/format';
import { StatusPill } from '../components/StatusPill';
import { statusLabel } from '../lib/roles';
import { cn } from '../lib/utils';
import { EmptyState } from '../components/common';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { CURRENCY_OPTIONS } from '../lib/currency';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from '../components/ui/sheet';

const CONTRACT_TYPES = ['Lumpsum', 'Remeasurable', 'Hourly Rates', 'Design & Build'];

// Dropdown bound to a reference master, with optional inline "+ Add" for managers.
function RefSelect({ kind, value, items, onChange, canManage, onAdded, placeholder, testid }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const active = items.filter((i) => i.active !== false);
  const submit = async () => {
    const name = text.trim();
    if (!name) return;
    try {
      const { data } = await api.post(`/refs/${kind}`, { name });
      setText(''); setAdding(false);
      await onAdded();
      onChange(data.name);
    } catch { toast.error(`Could not add ${kind === 'clients' ? 'client' : 'sector'}`); }
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Select value={value || '__none'} onValueChange={(v) => onChange(v === '__none' ? '' : v)}>
          <SelectTrigger data-testid={testid} className="flex-1"><SelectValue placeholder={placeholder} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">{placeholder}</SelectItem>
            {active.map((i) => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManage && (
          <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0"
            data-testid={`${testid}-add`} onClick={() => setAdding((a) => !a)}><Plus size={15} /></Button>
        )}
      </div>
      {adding && (
        <div className="flex items-center gap-1.5">
          <Input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={`New ${kind === 'clients' ? 'client' : 'sector'} name`}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} data-testid={`${testid}-add-input`} />
          <Button type="button" size="icon" className="h-9 w-9 shrink-0" onClick={submit}><Check size={15} /></Button>
        </div>
      )}
    </div>
  );
}

export default function Projects() {
  const { can, user } = useAuth();
  const canCreate = can('tenders', 'readwrite');
  const canManageRefs = can('tenders', 'approve');
  const canSetOwner = can('users', 'read');
  const isAdmin = user?.groupName === 'Admin';
  const [view, setView] = useState('active'); // 'active' | 'archived' (Admin only)
  const [archived, setArchived] = useState([]);
  const [archiveTarget, setArchiveTarget] = useState(null); // project pending archive confirmation
  const [actionBusy, setActionBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const activeUsers = useMemo(() => users.filter((u) => u.active !== false), [users]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'updatedAt', dir: 'desc' });
  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const blankForm = { name: '', reference: '', client: '', sector: '', contractType: '', location: '', currency: 'AED', submissionDueDate: '', indicativeValue: '', indicativeUnit: 'weeks', ownerId: '' };
  const [form, setForm] = useState(blankForm);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get('/projects');
    setProjects(data);
  }, []);
  const loadArchived = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get('/projects/archived');
      setArchived(data || []);
    } catch (e) {
      // Non-admins never reach here (button/tab hidden); log for debuggability.
      console.warn('Failed to load archived projects:', e);
    }
  }, [isAdmin]);
  const loadRefs = useCallback(async () => {
    const [c, s] = await Promise.all([api.get('/refs/clients'), api.get('/refs/sectors')]);
    setClients(c.data); setSectors(s.data);
  }, []);
  useEffect(() => { load(); loadRefs(); }, [load, loadRefs]);
  useEffect(() => { if (view === 'archived') loadArchived(); }, [view, loadArchived]);
  useEffect(() => {
    if (canSetOwner) api.get('/users').then((r) => setUsers(r.data || [])).catch((e) => console.warn('Failed to load users list:', e));
  }, [canSetOwner]);
  useEffect(() => {
    api.get('/settings').then((r) => {
      const dc = (r.data?.currency || 'AED').toUpperCase();
      setForm((f) => ({ ...f, currency: dc }));
    }).catch((e) => console.warn('Failed to load settings (using default currency):', e));
  }, []);
  useEffect(() => { if (params.get('new')) { setOpen(true); setParams({}); } }, [params, setParams]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Tender name is required'); return; }
    if (form.submissionDueDate && Number.isNaN(Date.parse(form.submissionDueDate))) { toast.error('Invalid submission due date'); return; }
    try {
      const payload = {
        name: form.name, reference: form.reference, client: form.client, sector: form.sector,
        contractType: form.contractType, location: form.location, currency: form.currency,
        submissionDueDate: form.submissionDueDate,
        indicativeDuration: form.indicativeValue ? { value: form.indicativeValue, unit: form.indicativeUnit } : null,
        ownerId: form.ownerId || undefined,
        saleItems: [],
      };
      const { data } = await api.post('/projects', payload);
      toast.success(`${data.code} created`);
      setOpen(false);
      setForm((f) => ({ ...blankForm, currency: f.currency || 'AED' }));
      navigate(`/projects/${data.id}`);
    } catch { toast.error('Could not create tender'); }
  };

  // Admin-only: soft-archive (hides from all active views, data retained).
  const doArchive = async (p) => {
    setActionBusy(true);
    try {
      await api.post(`/projects/${p.id}/archive`);
      toast.success(`${p.code} archived`);
      setArchiveTarget(null);
      await Promise.all([load(), loadArchived()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not archive tender');
    } finally { setActionBusy(false); }
  };
  // Admin-only: restore an archived project back into active views.
  const doRestore = async (p) => {
    setActionBusy(true);
    try {
      await api.post(`/projects/${p.id}/restore`);
      toast.success(`${p.code} restored`);
      await Promise.all([load(), loadArchived()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not restore tender');
    } finally { setActionBusy(false); }
  };

  const STATUSES = ['Draft', 'SubmittedToManager', 'SubmittedToAdmin', 'Approved', 'Rejected', 'Sent', 'Won', 'Lost'];
  const isArchivedView = view === 'archived' && isAdmin;
  const sourceProjects = isArchivedView ? archived : projects;
  const owners = [...new Set(sourceProjects.map((p) => p.ownerName).filter(Boolean))].sort();
  const NUMERIC = new Set(['quotationPrice', 'marginPct']);
  const toggleSort = (key) => setSort((s) => (s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: NUMERIC.has(key) || key === 'updatedAt' ? 'desc' : 'asc' }));

  const filtered = sourceProjects.filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (ownerFilter !== 'all' && p.ownerName !== ownerFilter) return false;
    return `${p.code} ${p.name} ${p.client}`.toLowerCase().includes(q.toLowerCase());
  });
  const dir = sort.dir === 'asc' ? 1 : -1;
  const rows = [...filtered].sort((a, b) => {
    let av = a[sort.key]; let bv = b[sort.key];
    if (NUMERIC.has(sort.key)) return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
    av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase();
    return av < bv ? -dir : av > bv ? dir : 0;
  });

  const SortIcon = ({ k }) => (sort.key === k
    ? (sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
    : <ArrowUpDown size={12} className="opacity-40" />);
  const Th = ({ k, label, className }) => (
    <th className={cn('px-4 py-3 font-semibold', className)}>
      <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
        {label}<SortIcon k={k} />
      </button>
    </th>
  );

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Tender Register</h1>
          <p className="text-sm text-muted-foreground">All estimation tenders — sortable, filterable, click any row to open.</p>
        </div>
        <div className="flex items-center gap-2">
          {canManageRefs && (
            <Button variant="outline" data-testid="manage-lists-button" onClick={() => setManageOpen(true)}>
              <Settings2 size={15} className="mr-2" />Manage lists
            </Button>
          )}
          {canCreate && (
            <Button data-testid="new-project-button" onClick={() => setOpen(true)} className="bg-[hsl(var(--brand))] text-white hover:brightness-110">
              <Plus size={15} className="mr-2" />New Tender
            </Button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="inline-flex rounded-lg border bg-muted/40 p-1" data-testid="archive-view-toggle">
          {[
            { key: 'active', label: 'Active' },
            { key: 'archived', label: 'Archived' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`archive-view-${t.key}`}
              onClick={() => { setView(t.key); setStatusFilter('all'); setOwnerFilter('all'); }}
              className={cn(
                'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                view === t.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
              {t.key === 'archived' && archived.length > 0 && (
                <span className="ml-1.5 rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">{archived.length}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="projects-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or client…" className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="register-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        {owners.length > 1 && (
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-[170px]" data-testid="register-owner-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {rows.length === 0 ? (
        <EmptyState testid="projects-empty-state" icon={isArchivedView ? Archive : FolderKanban}
          title={isArchivedView ? 'No archived tenders' : 'No tenders found'}
          description={isArchivedView
            ? 'Archived tenders are hidden from active views. Anything you archive will appear here.'
            : (q || statusFilter !== 'all' || ownerFilter !== 'all' ? 'Try clearing the search or filters.' : 'Create your first estimation tender to start building Sale and Cost BOQs.')}
          action={!isArchivedView && canCreate && !q && statusFilter === 'all' && ownerFilter === 'all' ? <Button onClick={() => setOpen(true)}><Plus size={15} className="mr-2" />New Tender</Button> : null} />
      ) : (
        <div className="erak-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tender-register-table">
              <thead><tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <Th k="code" label="Code" />
                <Th k="client" label="Client" />
                <Th k="reference" label="Reference" />
                <Th k="sector" label="Sector" />
                <Th k="contractType" label="Contract" />
                <Th k="quotationPrice" label="Quotation" className="text-right" />
                <Th k="marginPct" label="Margin %" className="text-right" />
                <Th k="status" label="Status" />
                <Th k="ownerName" label="Owner" />
                <Th k="submissionDueDate" label="Due" />
                <Th k="updatedAt" label="Updated" />
                {isAdmin && <th className="px-4 py-3 text-right font-semibold">Actions</th>}
              </tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} data-testid={`tender-row-${p.id}`} onClick={() => navigate(`/projects/${p.id}`)}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-2.5 font-num text-[hsl(var(--copper))]">{p.code}</td>
                    <td className="px-4 py-2.5"><div className="font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.client || '—'}</div></td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{p.reference || '—'}</td>
                    <td className="px-4 py-2.5 text-xs">{p.sector || '—'}</td>
                    <td className="px-4 py-2.5 text-xs">{p.contractType || '—'}</td>
                    <td className="px-4 py-2.5 text-right money font-medium">{fmtMoneyLabel(p.quotationPrice, p.currency)}</td>
                    <td className={cn('px-4 py-2.5 text-right font-num', p.marginPct < 15 ? 'text-[hsl(var(--danger))]' : 'text-[hsl(var(--success))]')}>{p.marginPct}%</td>
                    <td className="px-4 py-2.5"><StatusPill status={p.status} /></td>
                    <td className="px-4 py-2.5 text-xs">{p.ownerName}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{p.submissionDueDate ? fmtDate(p.submissionDueDate) : '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDate(p.updatedAt)}</td>
                    {isAdmin && (
                      <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {isArchivedView ? (
                          <Button variant="outline" size="sm" className="h-8" disabled={actionBusy}
                            data-testid={`restore-project-${p.id}`} onClick={() => doRestore(p)}>
                            <ArchiveRestore size={14} className="mr-1.5" />Restore
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-[hsl(var(--danger))]"
                            data-testid={`archive-project-${p.id}`} onClick={() => setArchiveTarget(p)}>
                            <Archive size={14} className="mr-1.5" />Archive
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent data-testid="new-project-sheet" className="flex w-full flex-col sm:max-w-md">
          <SheetHeader><SheetTitle>New Tender</SheetTitle></SheetHeader>
          <div className="mt-6 flex-1 space-y-4 overflow-y-auto pr-1">
            <div className="space-y-1.5"><Label>Tender name *</Label>
              <Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. RAKEZ 11kV Substation Package" /></div>
            <div className="space-y-1.5"><Label>Tender Reference</Label>
              <Input data-testid="project-reference-input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Client RFQ / enquiry no." /></div>
            <div className="space-y-1.5"><Label>Client</Label>
              <RefSelect kind="clients" testid="project-client-select" value={form.client} items={clients} canManage={canManageRefs}
                onChange={(v) => setForm({ ...form, client: v })} onAdded={loadRefs} placeholder="Select client" /></div>
            <div className="space-y-1.5"><Label>Sector / Scope Type</Label>
              <RefSelect kind="sectors" testid="project-sector-select" value={form.sector} items={sectors} canManage={canManageRefs}
                onChange={(v) => setForm({ ...form, sector: v })} onAdded={loadRefs} placeholder="Select sector" /></div>
            <div className="space-y-1.5"><Label>Contract Type</Label>
              <Select value={form.contractType || '__none'} onValueChange={(v) => setForm({ ...form, contractType: v === '__none' ? '' : v })}>
                <SelectTrigger data-testid="project-contract-select"><SelectValue placeholder="Select contract type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Select contract type</SelectItem>
                  {CONTRACT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Indicative Duration</Label>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" className="w-24 font-num" data-testid="project-duration-value"
                  value={form.indicativeValue} onChange={(e) => setForm({ ...form, indicativeValue: e.target.value })} placeholder="e.g. 12" />
                <Select value={form.indicativeUnit} onValueChange={(v) => setForm({ ...form, indicativeUnit: v })}>
                  <SelectTrigger className="w-[120px]" data-testid="project-duration-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weeks">Weeks</SelectItem>
                    <SelectItem value="months">Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-muted-foreground">Tender-stage estimate. Firm start/end dates are set at award.</p>
            </div>
            <div className="space-y-1.5"><Label>Submission Due Date</Label>
              <Input type="date" data-testid="project-due-input" value={form.submissionDueDate} onChange={(e) => setForm({ ...form, submissionDueDate: e.target.value })} /></div>
            {canSetOwner && (
              <div className="space-y-1.5"><Label>Estimator / Owner</Label>
                <Select value={form.ownerId || '__me'} onValueChange={(v) => setForm({ ...form, ownerId: v === '__me' ? '' : v })}>
                  <SelectTrigger data-testid="project-owner-select"><SelectValue placeholder="Defaults to me" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__me">Me (default)</SelectItem>
                    {activeUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
            <div className="space-y-1.5"><Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. RAKEZ, Ras Al Khaimah" /></div>
            <div className="space-y-1.5"><Label>Currency</Label>
              <Select value={(form.currency || 'AED').toUpperCase()} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="project-currency-select" className="font-num"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <SheetFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="create-project-submit" onClick={create}>Create Tender</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ManageListsDialog open={manageOpen} onOpenChange={setManageOpen}
        clients={clients} sectors={sectors} onChanged={loadRefs} />

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {archiveTarget?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              This hides <span className="font-medium text-foreground">{archiveTarget?.name}</span> from all active
              views, dashboards and lists. No data is deleted — you can restore it any time from the Archived tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="archive-confirm" disabled={actionBusy}
              onClick={(e) => { e.preventDefault(); if (archiveTarget) doArchive(archiveTarget); }}
              className="bg-[hsl(var(--danger))] text-white hover:brightness-110">
              <Archive size={14} className="mr-1.5" />Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Admin/Management dialog to add, rename and activate/deactivate Clients & Sectors.
function ManageListsDialog({ open, onOpenChange, clients, sectors, onChanged }) {
  const [tab, setTab] = useState('clients');
  const [newName, setNewName] = useState('');
  const items = tab === 'clients' ? clients : sectors;
  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try { await api.post(`/refs/${tab}`, { name }); setNewName(''); await onChanged(); }
    catch { toast.error('Could not add'); }
  };
  const rename = async (it) => {
    const name = window.prompt('Rename to:', it.name);
    if (name == null || !name.trim()) return;
    try { await api.put(`/refs/${tab}/${it.id}`, { name: name.trim() }); await onChanged(); }
    catch { toast.error('Could not rename'); }
  };
  const toggle = async (it) => {
    try { await api.put(`/refs/${tab}/${it.id}`, { active: it.active === false }); await onChanged(); }
    catch { toast.error('Could not update'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manage-lists-dialog" className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Manage Clients &amp; Sectors</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {['clients', 'sectors'].map((t) => (
            <Button key={t} variant={tab === t ? 'default' : 'outline'} size="sm" onClick={() => setTab(t)} data-testid={`manage-tab-${t}`}>
              {t === 'clients' ? 'Clients' : 'Sectors'}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={`New ${tab === 'clients' ? 'client' : 'sector'} name`}
            data-testid="manage-new-input" onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
          <Button onClick={add} data-testid="manage-add-button"><Plus size={15} className="mr-1" />Add</Button>
        </div>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No entries yet.</p>}
          {items.map((it) => (
            <div key={it.id} data-testid={`manage-row-${it.id}`} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2">
              <span className={cn('text-sm', it.active === false && 'text-muted-foreground line-through')}>{it.name}</span>
              <div className="flex items-center gap-3">
                <button className="text-xs text-[hsl(var(--teal))] hover:underline" onClick={() => rename(it)}>Rename</button>
                <div className="flex items-center gap-1.5">
                  <Switch checked={it.active !== false} onCheckedChange={() => toggle(it)} aria-label="Active" />
                  <span className="text-[11px] text-muted-foreground">{it.active === false ? 'Inactive' : 'Active'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
